<script setup>
import {
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/vue/24/outline';
import { integer } from '@vuelidate/validators';
</script>

<template>
  <tr class="group cursor-pointer" @click="goToDemo()">
    <td class="whitespace-nowrap text-sm py-3 pl-[18px]">
      <input
        type="checkbox"
        :value="checkboxVal"
        v-bind:checked="checkboxVal"
        v-on:input="updateSelected(demo)"
        class="h-4 w-4 rounded border-2 border-gray-400 text-indigo-600 focus:ring-indigo-500 cursor-pointer bg-gray-900"
        @click.stop
      />
    </td>
    <td class="whitespace-nowrap text-sm items-center py-3 pr-4">
      <div v-if="demo.cs_map" class="flex relative gap-3">
        <img
          class="w-12 h-12 rounded-md object-cover opacity-[25%]"
          :src="`/maps/${convertMapName(demo.cs_map.name)}_cs2.jpg`"
        />
        <img
          class="w-8 h-8 rounded-md object-cover absolute top-0 left-0 translate-x-1/4 translate-y-1/4"
          :src="`/maps/map_icon_${convertMapName(demo.cs_map.name)}.svg`"
        />
        <div class="flex gap-px justify-center flex-col items-start">
          <h1 class="text-text-primary text-sm">{{ demo.team_scores?.CT || "X" }} - {{ demo.team_scores?.T || "X" }}</h1>
          <h1 class="text-text-secondary text-xs flex gap-1 items-center">
            <img src="../../../assets/logos/refrag-icon.svg" class="w-3 h-3 brightness-75" />
            <span class="text-text-secondary text-xs">{{ demo.type || $t("restrat.demo_row.default_type") }}</span>
          </h1>
        </div>
      </div>
      <span class="text-text-primary text-sm hidden lg:block" v-else>-</span>
    </td>
    <td
      class="whitespace-nowrap text-sm text-text-primary py-3 text-ellipsis overflow-hidden pr-4"
    >
      <span v-if="validateDemo() && hasTeamNames">
        {{ demo.team_names.CT }}
        vs
        {{ demo.team_names.T }}
      </span>
      <span v-else>{{ $t("restrat.demo_row.placeholder_teams") }}</span>
    </td>
    <td class="whitespace-nowrap text-sm text-text-primary py-3 pr-4">
      <span v-if="validateDemo() && hasTeamScores">
        {{ demo.team_scores.CT }}:{{ demo.team_scores.T }}
      </span>
      <span v-else>-</span>
    </td>
    <td
      class="whitespace-nowrap text-sm text-text-primary py-3 overflow-hidden text-ellipsis max-w-12 lg:max-w-none"
    >
      <span>
        {{ moment(demo.created_at).format("MMM Do YYYY") }}
      </span>
    </td>
    <td class="text-center flex gap-2 justify-end pt-5 group-last:pb-5">
      <div
        v-if="demo.download_url"
        class="inline-flex items-center justify-center items-center h-8 w-8 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
        @click="downloadDemo()"
      >
        <ArrowDownTrayIcon class="w-4 h-4 text-text-primary" />
      </div>
      <a
        @click="goToDemo()"
        class="inline-flex items-center justify-center mr-4 items-center w-8 h-8 border border-transparent rounded-md shadow-sm text-sm font-medium"
        :class="
          demoInvalid
            ? 'bg-gray-400 text-black has-tooltip'
            : 'bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 cursor-pointer'
        "
        :disabled="demoInvalid"
      >
        <img src="../../../assets/icons/rightarrow.svg" class="w-4 h-4 brightness-200" :alt="$t('restrat.demo_row.play_icon_alt')" />
        <span class="tooltip rounded shadow-md p-1 bg-gray-700 text-white mt-36 py-2 px-4"
        >{{ $t("restrat.demo_row.failed_tooltip") }}</span
        >
      </a>
    </td>
  </tr>
</template>

<script>
import axios from 'axios';
import { mapMutations, mapState } from 'vuex';
import moment from 'moment';

export default {
  name: 'DemoRow',
  data() {
    return {
      checkboxVal: false,
    };
  },
  props: {
    demo: {},
    allSelected: Boolean,
    selectedDemos: [],
    numOfDemos: integer,
  },
  watch: {
    // Watching and updating child checkboxes depending on status of checkall checkbox in parent
    allSelected(newValue) {
      if (!newValue) {
        this.checkboxVal = false;
        this.$emit('remove-selected-demo', this.demo.id);
      } else if (newValue && !this.checkboxVal) {
        this.checkboxVal = true;
        this.$emit('push-selected-demo', this.demo);
      }
    },
  },
  computed: {
    user() {
      return this.$auth.user() || {};
    },
    team() {
      // Checks to see which team to display based on activeTeamId
      for (let i = 0; i < this.user.teams.length; i++) {
        if (this.user.teams[i].id === Number(localStorage.activeTeamId)) {
          return this.user.teams[i];
        }
      }
      return this.user.teams[0] || {};
    },
    hasTeamNames() {
      return this.demo.team_names && this.demo.team_names.CT && this.demo.team_names.T;
    },
    hasTeamScores() {
      return this.demo.team_scores && this.demo.team_scores.CT && this.demo.team_scores.T;
    },
    demoInvalid() {
      return (
        this.demo.status === 'broken'
        || this.demo.status === 'analysis_failed'
        || this.demo.status === 'failed'
      );
    },
  },
  methods: {
    updateSelected(demo) {
      this.checkboxVal = !this.checkboxVal;
      if (this.checkboxVal) {
        this.$emit('push-selected-demo', demo);
      } else {
        this.$emit('remove-selected-demo', demo.id);
      }
      if (this.allSelected) this.$emit('update-all-selected', false);
    },
    ...mapMutations(['showMessage', 'hideMessage']),
    ...mapState(['activeTeam']),
    goToDemo: function goToDemo() {
      if (this.demo.status === 'broken' || this.demo.status === 'analysis_failed') {
        return false;
      }
      return this.$router.push({
        name: 'DemoPage',
        params: { id: this.demo.id },
      });
    },
    playDemo: function playDemo() {
      if (this.demoInvalid) {
        return false;
      }

      return this.$router.push({
        name: 'ReviewPage',
        params: { id: this.demo.id },
      });
    },
    convertMapName(name) {
      return name?.includes('wm_') ? name.replace('wm_', 'de_') : name;
    },
    downloadDemo() {
      window.open(this.demo.download_url, '_blank');
    },
    showMessagePopup(messageType, messageText, hideAutomatically = false) {
      this.showMessage({ messageType, messageText });
      if (hideAutomatically) {
        setTimeout(this.hideMessage, 3000);
      }
    },
    validateDemo: function validateDemo() {
      return (
        (this.demo && this.demo.status === 'ready')
        || this.demo.status === 'analyzing'
        || this.demo.status === 'uploaded'
      );
    },
  },
};
</script>

<style lang="scss"></style>
